import { useEffect } from 'react';
import { useWizard } from '../../../context/WizardContext';
import { NavigationButtons } from '../../../components/wizard/NavigationButtons';
import { DEPARTMENTS, requiresDepartment } from '../../../types/wizard';
import type { Department } from '../../../types/wizard';
import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';
import { DEPARTMENT_NAME_KEYS } from '../../../i18n';
import { Check } from 'lucide-react';

// Translucent tints, not solid `-50` fills — the wizard sits on a dark surface
// where a light fill reads as a blown-out card.
const COLOR_MAP: Record<
  string,
  {
    selectedBg: string;
    selectedBorder: string;
    selectedText: string;
    gradient: string;
  }
> = {
  blue: {
    selectedBg: 'bg-blue-500/10',
    selectedBorder: 'border-blue-500/50',
    selectedText: 'text-blue-300',
    gradient: 'from-blue-400 to-blue-600',
  },
  emerald: {
    selectedBg: 'bg-emerald-500/10',
    selectedBorder: 'border-emerald-500/50',
    selectedText: 'text-emerald-300',
    gradient: 'from-emerald-400 to-emerald-600',
  },
  violet: {
    selectedBg: 'bg-violet-500/10',
    selectedBorder: 'border-violet-500/50',
    selectedText: 'text-violet-300',
    gradient: 'from-violet-400 to-violet-600',
  },
};

export function Step1Department() {
  const { state, setDepartment, goNext } = useWizard();
  const { t } = useTranslation();
  const needsDept = requiresDepartment(state.selectedClass);

  useEffect(() => {
    if (!needsDept) goNext();
  }, [needsDept, goNext]);

  if (!needsDept) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-400 text-sm">
        {t('wizard.departmentSkipping')}
      </div>
    );
  }

  const classLabel = (() => {
    if (!state.selectedClass) return '';
    return state.selectedClass <= 10 ? t('wizard.levelSsc') : t('wizard.levelHsc');
  })();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">{t('wizard.departmentTitle')}</h2>
        <p className="text-sm text-slate-500 mt-1">
          {t('wizard.departmentSubtitle', {
            class: state.selectedClass ?? '',
            level: classLabel,
          })}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {DEPARTMENTS.map((dept) => {
          const colors = COLOR_MAP[dept.color];
          const isSelected = state.selectedDepartment === dept.id;
          return (
            <button
              key={dept.id}
              type="button"
              onClick={() => setDepartment(dept.id as Department)}
              className={cn(
                'relative flex flex-col items-center gap-4 rounded-2xl border-2 p-6 transition-all duration-200 focus:outline-none group',
                isSelected
                  ? `${colors.selectedBorder} ${colors.selectedBg} shadow-glow`
                  : 'border-slate-700 bg-slate-700/50 hover:border-slate-600 hover:shadow-card-hover hover:-translate-y-0.5'
              )}
            >
              {isSelected && (
                <span className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-white">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
              )}
              <div
                className={cn(
                  'flex h-14 w-14 items-center justify-center rounded-2xl text-2xl shadow-md transition-transform duration-200 group-hover:scale-105',
                  `bg-gradient-to-br ${colors.gradient}`
                )}
              >
                {dept.icon}
              </div>
              <div className="text-center">
                <p
                  className={cn(
                    'font-bold text-base',
                    isSelected ? colors.selectedText : 'text-white'
                  )}
                >
                  {t(DEPARTMENT_NAME_KEYS[dept.id])}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      <NavigationButtons />
    </div>
  );
}
